import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d7cdvchnoju8nk5a0psg";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus("loading");
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-400 mb-3 block">
            Contact
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Have questions about our products or want to collaborate? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="w-full lg:w-5/12 flex flex-col gap-6">
            <div className="bg-gray-800 rounded-2xl p-8">
              <h3 className="text-white font-semibold text-lg mb-6">Company Information</h3>

              <div className="flex flex-col gap-5">
                {/* Company Name */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-building-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Company</p>
                    <p className="text-white text-sm font-medium">IDEL SH.P.K.</p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Address</p>
                    <p className="text-white text-sm leading-relaxed">
                      Kashar, Mezez, ndertese me nr pasurie 440/45-ND, zk. 2679
                    </p>
                  </div>
                </div>

                {/* City */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-community-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">City</p>
                    <p className="text-white text-sm font-medium">TIRANE</p>
                  </div>
                </div>

                {/* Zip & Country */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-flag-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Country &amp; Zip</p>
                    <p className="text-white text-sm font-medium">Albania — 1000</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Email</p>
                    <p className="text-white text-sm font-medium break-all">info@idelshpk.com</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-white text-sm font-medium">+1 6624814834</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="w-full lg:w-7/12">
            <div className="bg-gray-800 rounded-2xl p-8">
              <h3 className="text-white font-semibold text-lg mb-6">Send a Message</h3>
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/10 mb-4">
                    <i className="ri-check-double-line text-emerald-400 text-3xl"></i>
                  </div>
                  <h4 className="text-white font-semibold text-lg mb-2">Message Sent!</h4>
                  <p className="text-gray-400 text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 text-emerald-400 text-sm hover:text-emerald-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="flex flex-col gap-5"
                >
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-gray-400 text-xs uppercase tracking-wider">Name</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-gray-400 text-xs uppercase tracking-wider">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-gray-400 text-xs uppercase tracking-wider">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What is this about?"
                      className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-gray-400 text-xs uppercase tracking-wider flex items-center justify-between">
                      <span>Message</span>
                      <span className={`text-xs ${formData.message.length > 500 ? "text-red-400" : "text-gray-500"}`}>
                        {formData.message.length}/500
                      </span>
                    </label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us how we can help..."
                      maxLength={500}
                      className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    />
                    {formData.message.length > 500 && (
                      <p className="text-red-400 text-xs">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {status === "error" && (
                    <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading" || formData.message.length > 500}
                    className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold rounded-lg px-6 py-3 text-sm transition-colors cursor-pointer whitespace-nowrap self-start"
                  >
                    {status === "loading" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
